import { Controller, useForm } from "react-hook-form";
import type { ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button2 } from "@/components/UI2/atoms/Button";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { testLocator } from "@/lib/helpers/test-locator";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { generalAddendumSchema, type GeneralAddendumFormValues } from "./GeneralAddendum.schema";

type GeneralAddendumFormProps = {
  children?: ReactNode;
  defaultValue?: string;
  isPending: boolean;
  mutate: (addendum: string) => void;
  onCancel: () => void;
};

export function GeneralAddendumForm({
  children,
  defaultValue,
  isPending,
  mutate,
  onCancel,
}: GeneralAddendumFormProps) {
  const { control, formState, handleSubmit } = useForm<GeneralAddendumFormValues>({
    defaultValues: { addendum: defaultValue ?? "" },
    mode: "onChange",
    resolver: zodResolver(generalAddendumSchema),
  });

  return (
    <form
      className="tw:flex tw:flex-col tw:gap-6"
      onSubmit={handleSubmit(({ addendum }) => mutate(addendum))}
      {...getAutomationDataAttr("note-addendum-form")}
      {...testLocator("noteAddendumForm")}
    >
      <Controller
        control={control}
        name="addendum"
        render={({ field, fieldState }) => (
          <TextField2
            aria-label="Details"
            errorMessage={fieldState.error?.message}
            isInvalid={!!fieldState.error}
            mode="textarea"
            onChange={field.onChange}
            placeholder="Add details..."
            textAreaProps={{
              className: "tw:h-32",
              ...getAutomationDataAttr("note-addendum-comment"),
              ...testLocator("noteAddendumComment"),
            }}
            value={field.value}
          />
        )}
      />

      {children}

      <div className="tw:flex tw:items-end tw:justify-end tw:gap-4">
        <Button2 onPress={onCancel} variant="secondary">
          Cancel
        </Button2>

        <Button2
          isDisabled={!formState.isValid}
          isLoading={isPending}
          labelLoading={null}
          type="submit"
          variant="primary"
          {...getAutomationDataAttr("note-addendum-save")}
          {...testLocator("noteAddendumSave")}
        >
          Save
        </Button2>
      </div>
    </form>
  );
}
